import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, errMsg } from "../../lib/api";
import { Star } from "lucide-react";
import { Stars } from "../ReviewStrip";

const COLOR = { pending: "bg-amber-100", approved: "bg-lime-200", hidden: "bg-slate-200" };

const EMPTY = { rating: 5, text: "", display_name: "", location: "" };
const INPUT = "w-full border-2 border-slate-900 px-3 py-2 text-sm";

const PostReview = ({ onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    adminApi().post("/admin/reviews", f).then(() => { toast.success("Review posted and live on the site"); setF(EMPTY); onDone(); })
      .catch((er) => toast.error(errMsg(er))).finally(() => setBusy(false));
  };
  return (
    <form onSubmit={submit} className="space-y-2 border-2 border-slate-900 bg-lime-50 p-4" data-testid="admin-post-review-form">
      <div className="font-bold uppercase">Post a review you received</div>
      <div className="flex items-center gap-1" data-testid="admin-post-review-rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => setF({ ...f, rating: n })} aria-label={`${n} stars`} data-testid={`admin-post-review-star-${n}`}>
            <Star className={`h-6 w-6 ${n <= f.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
      <textarea required maxLength={1200} rows={3} placeholder="What the customer said" value={f.text} onChange={up("text")} className={INPUT} data-testid="admin-post-review-text" />
      <div className="grid gap-2 sm:grid-cols-2">
        <input required maxLength={40} placeholder="Customer name" value={f.display_name} onChange={up("display_name")} className={INPUT} data-testid="admin-post-review-name" />
        <input maxLength={40} placeholder="Town or county (optional)" value={f.location} onChange={up("location")} className={INPUT} data-testid="admin-post-review-location" />
      </div>
      <button type="submit" disabled={busy} className="border-2 border-slate-900 bg-lime-300 px-4 py-2 text-xs font-bold uppercase disabled:opacity-50" data-testid="admin-post-review-submit">{busy ? "Posting…" : "Post review"}</button>
    </form>
  );
};

export const ReviewsAdmin = () => {
  const [d, setD] = useState(null);
  const load = useCallback(() => adminApi().get("/admin/reviews").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const set = (id, status) => adminApi().put(`/admin/reviews/${id}`, { status }).then(() => { toast.success(status === "approved" ? "Review is now live" : "Review hidden"); load(); }).catch((e) => toast.error(errMsg(e)));
  if (!d) return <p className="mt-6">Loading…</p>;
  return (
    <section className="mt-6 space-y-3" data-testid="admin-reviews">
      <p className="text-sm text-slate-600">Customers get a "rate your shield" email 4 days after their order is marked dispatched. Approve reviews to show them on the site and in Google results. {d.waiting} request{d.waiting === 1 ? "" : "s"} waiting for a reply.</p>
      <PostReview onDone={load} />
      {!d.reviews.length && <p className="border-2 border-dashed border-slate-300 p-6 text-center text-slate-500" data-testid="admin-reviews-empty">No reviews yet.</p>}
      {d.reviews.map((r) => (
        <div key={r.id} className="flex flex-wrap items-start justify-between gap-3 border-2 border-slate-900 bg-white p-4" data-testid={`admin-review-${r.id}`}>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2"><Stars value={r.rating} /><span className={`px-2 py-0.5 text-xs font-bold uppercase ${COLOR[r.status]}`}>{r.status}</span><span className="text-xs text-slate-500">{r.order_number}{r.email ? ` · ${r.email}` : ""}</span></div>
            <p className="mt-1 text-sm">{r.text || <em className="text-slate-400">No comment</em>}</p>
            <p className="text-xs font-bold text-slate-500">{r.display_name}{r.location ? `, ${r.location}` : ""}</p>
          </div>
          <div className="flex gap-2">
            {r.status !== "approved" && <button type="button" onClick={() => set(r.id, "approved")} className="border-2 border-slate-900 bg-lime-300 px-3 py-1 text-xs font-bold uppercase" data-testid={`admin-review-approve-${r.id}`}>Approve</button>}
            {r.status !== "hidden" && <button type="button" onClick={() => set(r.id, "hidden")} className="border-2 border-slate-900 bg-white px-3 py-1 text-xs font-bold uppercase" data-testid={`admin-review-hide-${r.id}`}>Hide</button>}
          </div>
        </div>
      ))}
    </section>
  );
};
